import {
  faFileAlt,
  faPencilAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addDisplayLocation,
  deleteDisplayLocation,
  editDisplayLocation,
  getDisplayLocationById,
  getDisplayLocations,
} from "src/store/slices/display-location.slice";
import * as yup from "yup";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import SearchBarComponent from "../../search-bar/search-bar.component";
import "../../../admin.scss";

interface DisplayLocationTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
}

const DisplayLocationTableComponent: FC<DisplayLocationTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
}) => {
  const formRef = createRef();
  const formRefForContacts = createRef();
  const blockDetailsModalRef: any = createRef();
  const locationModalRef: any = createRef();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [selectedIdToDeleteOrEdit, setSelectedIdToDeleteOrEdit] =
    useState<number>(0);
  /*   const [selectedDisplayLocationId, setSelectedDisplayLocationId] =
    useState<any>(null); */

  const [initialValues, setInitialValues] = useState<any>({
    name: "",
  });

  const dispatch = useDispatch();

  const displayLocationDetails = useSelector((state: any) => {
    return state.displayLocation.displayLocationDetails;
  });

  const isLoadingDisplayLocationDetails = useSelector((state: any) => {
    return state.displayLocation.isLoadingDisplayLocationDetails;
  });

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsDetails(false);
    setIsEdit(false);
    setIsDelete(false);

    setInitialValues({
      name: "",
    });
  };

  const AddAction = () => {
    handleCloseModal();

    setIsModalOpen(true);
  };

  const detailsAction = (id: any) => {
    setIsDetails(true);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(false);

    dispatch(getDisplayLocationById({ Id: id }));

    setSelectedIdToDeleteOrEdit(id);
  };

  const editAction = (id: any) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(true);
    setIsDelete(false);

    dispatch(getDisplayLocationById({ Id: id }));

    setSelectedIdToDeleteOrEdit(id);
  };

  const deleteAction = (id: any) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(true);

    setSelectedIdToDeleteOrEdit(id);
  };

  const handleDelete = () => {
    dispatch(deleteDisplayLocation({ Id: selectedIdToDeleteOrEdit })).then(
      () => {
        onRefetchData();
        handleCloseModal();
      }
    );
  };

  useEffect(() => {
    if (!displayLocationDetails || !Object.keys(displayLocationDetails).length)
      return;

    setInitialValues({
      name: displayLocationDetails?.name || "",
    });
  }, [displayLocationDetails]);

  const onSubmit = () => {
    const form: any = formRef.current;
    if (form) {
      form.handleSubmit();
      const { name } = form.values;

      // Check if name is empty or exceeds 50 characters
      if (!name || name?.length > 50) {
        return;
      }

      if (isEdit) {
        const formValues = form.values;

        const payload = {
          ...formValues,
          id: selectedIdToDeleteOrEdit,
        };

        dispatch(editDisplayLocation({ payload })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      } else {
        dispatch(addDisplayLocation({ payload: form.values })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      }
    }

    return;
  };

  const modalActionConfig: any = isDetails
    ? []
    : isDelete
    ? [
        {
          label: "Yes",
          event: handleDelete,
          variant: "primary",
        },
      ]
    : [
        {
          label: "Save",
          event: onSubmit,
          variant: "primary",
        },
      ];

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "id",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Location",
      actionName: "Edit-Location",
      icon: faPencilAlt,
    },
    {
      label: "View",
      actionProperty: "id",
      event: detailsAction,
      variant: "info",
      className: "mx-2 my-1",
      moduleName: "Location",
      actionName: "View-Location",
      icon: faFileAlt,
    },
    {
      label: "Delete",
      actionProperty: "id",
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Location",
      actionName: "Delete-Location",
      icon: faTrashAlt,
    },
  ];

  const emptyConfig = {
    title: "No Location Found",
    description: "",
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "name",
        label: "Location Name",
        placeholder: "Enter Location Name",
        type: "text",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
    ],
  };

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required("Location Name is required")
      .max(50, "Location Name must be at most 50 characters"),
  });

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-generic-action table-toolbar">
          <SearchBarComponent
            placeholder="Search Location"
            methodToDispatch={getDisplayLocations}
            getSearch={onSearch}
          />
          <HasPermission
            moduleName="Location"
            actionName="Add-Location"
            isRoute={false}
          >
            <B2Button
              className="icon-text-btn"
              variant="primary"
              onClick={() => AddAction()}
            >
              Add New Location
            </B2Button>
          </HasPermission>
        </div>
        <div className="my-3">
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}

      {isModalOpen && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={
            isDetails
              ? "Location Details"
              : isEdit
              ? "Edit Location"
              : isDelete
              ? "Delete Location"
              : "Add New Location"
          }
          actionConfig={modalActionConfig}
        >
          {isDelete ? (
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Delete</b>?
            </h5>
          ) : (
            <SkeletonComponent
              wrapperHeight="280px"
              skeletonHeight="50px"
              isLoading={isLoadingDisplayLocationDetails}
              count={3}
            >
              <FormBuilderComponent
                formRef={formRef}
                initialValues={initialValues}
                validation={validationSchema}
                formConfig={formConfig}
              />
            </SkeletonComponent>
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default DisplayLocationTableComponent;
